import { useCallback, useEffect, useRef, useState } from "react";
import {
  Check,
  ChevronRight,
  CircleHelp,
  Folder,
  Inbox,
  KanbanSquare,
  LayoutGrid,
  ListFilter,
  LoaderCircle,
  LogOut,
  Monitor,
  Plus,
  Search,
  Settings2,
  ShieldCheck,
  X,
} from "lucide-react";
import type { BoardData, Task } from "../../server/src/models";
import { api } from "./api";
import { type Notify, IconButton, Badge, Modal } from "./components/ui";
import { Login } from "./components/Login";
import { LaneColumn } from "./components/Board";
import { NewTask, NewProject } from "./components/TaskForms";
import { TaskView } from "./components/TaskView";
import { AgentIdentities } from "./components/AgentIdentities";
import { SettingsPanel } from "./components/Settings";

export function App() {
  const [auth, setAuth] = useState<{
      authenticated: boolean;
      setup_required: boolean;
      setup_allowed: boolean;
    } | null>(null),
    [authError, setAuthError] = useState("");
  const [project, setProject] = useState(
      localStorage.getItem("mk-project") ?? "mini-kanban",
    ),
    [board, setBoard] = useState<BoardData | null>(null),
    [error, setError] = useState("");
  const [query, setQuery] = useState(""),
    [inbox, setInbox] = useState(false),
    [machine, setMachine] = useState(""),
    [tag, setTag] = useState(""),
    [archived, setArchived] = useState(false);
  const [newTask, setNewTask] = useState<string | null>(null),
    [settings, setSettings] = useState(false),
    [identities, setIdentities] = useState(false),
    [settingsTab, setSettingsTab] = useState("guidance"),
    [newProject, setNewProject] = useState(false);
  const [selected, setSelected] = useState<string | null>(
      decodeURIComponent(
        location.pathname.match(/^\/tasks\/([^/]+)/)?.[1] ?? "",
      ) || null,
    ),
    [fullPage, setFullPage] = useState(
      !!location.pathname.match(/^\/tasks\//) &&
        !location.search.includes("panel=1"),
    );
  const [toast, setToast] = useState<{
      message: string;
      error: boolean;
    } | null>(null),
    [live, setLive] = useState(false),
    [revision, setRevision] = useState(0),
    [viewName, setViewName] = useState<string | null>(null);
  const notify: Notify = useCallback(
    (message, error = false) => setToast({ message, error }),
    [],
  );
  const checkAuth = useCallback(() => {
    api<typeof auth>("/auth/status")
      .then(setAuth)
      .catch((e) => setAuthError(e.message));
  }, []);
  useEffect(checkAuth, [checkAuth]);
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(timer);
  }, [toast]);
  const refreshSequence = useRef(0);
  const refresh = useCallback(async () => {
    const sequence = ++refreshSequence.current;
    if (!auth?.authenticated) return;
    try {
      const params = new URLSearchParams({
        q: query,
        ...(inbox ? { needs_human: "1" } : {}),
        ...(machine ? { machine } : {}),
        ...(tag ? { tag } : {}),
        ...(archived ? { include_archived: "1" } : {}),
      });
      const data = await api<BoardData>(
        `/v1/projects/${project}/board?${params}`,
      );
      if (sequence !== refreshSequence.current) return;
      setBoard(data);
      setError("");
    } catch (e) {
      if (sequence === refreshSequence.current) setError((e as Error).message);
    }
  }, [auth?.authenticated, project, query, inbox, machine, tag, archived]);
  useEffect(() => {
    const timer = setTimeout(() => void refresh(), 180);
    return () => clearTimeout(timer);
  }, [refresh, revision]);
  useEffect(() => {
    if (!auth?.authenticated) return;
    const stream = new EventSource("/v1/events/sse");
    let timer: ReturnType<typeof setTimeout> | undefined;
    stream.onopen = () => setLive(true);
    stream.onerror = () => setLive(false);
    stream.addEventListener("change", () => {
      clearTimeout(timer);
      timer = setTimeout(() => setRevision((n) => n + 1), 120);
    });
    const poll = setInterval(() => {
      setRevision((n) => n + 1);
      checkAuth();
    }, 30000);
    return () => {
      stream.close();
      clearTimeout(timer);
      clearInterval(poll);
    };
  }, [auth?.authenticated, checkAuth]);
  useEffect(() => {
    const update = () => {
      setSelected(
        decodeURIComponent(
          location.pathname.match(/^\/tasks\/([^/]+)/)?.[1] ?? "",
        ) || null,
      );
      setFullPage(!location.search.includes("panel=1"));
    };
    window.addEventListener("popstate", update);
    return () => window.removeEventListener("popstate", update);
  }, []);
  function chooseProject(key: string) {
    setProject(key);
    localStorage.setItem("mk-project", key);
    setQuery("");
    setTag("");
    setMachine("");
  }
  const openTask = useCallback((taskId: string, page = false) => {
    setSelected(taskId);
    setFullPage(page);
    history.pushState({}, "", `/tasks/${taskId}${page ? "" : "?panel=1"}`);
  }, []);
  const closeTask = useCallback(() => {
    setSelected(null);
    setFullPage(false);
    history.pushState({}, "", "/");
  }, []);
  const changed = useCallback(() => {
    setRevision((n) => n + 1);
  }, []);
  async function moveTask(task: Task, lane: string) {
    if (task.lane === lane) return;
    if (task.lane === "ready" && lane === "backlog") {
      openTask(task.id);
      notify("Use Send back on the task to add re-triage instructions.");
      return;
    }
    try {
      await api(`/v1/tasks/${task.id}/move`, "POST", { lane });
      changed();
      notify(
        `${task.id} moved to ${board?.lanes.find((l) => l.key === lane)?.title ?? lane}.`,
      );
    } catch (e) {
      notify((e as Error).message, true);
    }
  }
  async function saveView() {
    if (!viewName?.trim()) return;
    try {
      await api(`/v1/projects/${project}/views`, "POST", {
        name: viewName,
        filters: {
          q: query,
          needs_human: inbox ? "1" : "0",
          machine,
          tag,
          include_archived: archived ? "1" : "0",
        },
      });
      setViewName(null);
      changed();
      notify("View saved.");
    } catch (e) {
      notify((e as Error).message, true);
    }
  }
  if (!auth)
    return (
      <div className="loading-page">
        {authError ? (
          <>
            <CircleHelp />
            <p>{authError}</p>
            <button onClick={checkAuth}>Try again</button>
          </>
        ) : (
          <>
            <img src="/favicon.svg" alt="" />
            <p>Opening your workspace…</p>
            <LoaderCircle className="spin" />
          </>
        )}
      </div>
    );
  if (!auth.authenticated)
    return auth.setup_required && !auth.setup_allowed ? (
      <div className="loading-page">
        <ShieldCheck />
        <h2>Admin setup required</h2>
        <p>
          Open the one-time setup link provided with your board to choose your
          password.
        </p>
      </div>
    ) : (
      <Login setup={auth.setup_required} onLogin={checkAuth} />
    );
  const filtersActive = !!query || !!machine || !!tag || archived;
  return (
    <div className="app">
      <aside className="sidebar">
        <a
          className="brand"
          aria-label="Mini Kanban home"
          href="/"
          onClick={(e) => {
            e.preventDefault();
            closeTask();
          }}
        >
          <img src="/favicon.svg" alt="" />
          <span>
            Mini Kanban
            <span className="brand-sub">A home for your next step</span>
          </span>
        </a>
        <div className="workspace-label">
          <span className={`dot ${live ? "green" : "gray"}`} />
          {live ? "Connected to your board" : "Reconnecting…"}
        </div>
        <nav>
          <button
            className={`nav-item ${!inbox ? "active" : ""}`}
            onClick={() => {
              setInbox(false);
              closeTask();
            }}
          >
            <LayoutGrid size={17} />
            All tasks<span className="nav-count">{board?.counts.all ?? 0}</span>
          </button>
          <button
            className={`nav-item ${inbox ? "active" : ""}`}
            onClick={() => {
              setInbox(true);
              closeTask();
            }}
          >
            <Inbox size={17} />
            Needs you
            <span
              className={`nav-count ${board?.counts.needs_human ? "purple" : ""}`}
            >
              {board?.counts.needs_human ?? 0}
            </span>
          </button>
        </nav>
        <div className="sidebar-section">
          <div className="section-label">
            PROJECTS
            <IconButton label="New project" onClick={() => setNewProject(true)}>
              <Plus size={14} />
            </IconButton>
          </div>
          {board?.projects.map((p) => (
            <button
              key={p.key}
              className={`project-item ${p.key === project ? "selected" : ""}`}
              onClick={() => chooseProject(p.key)}
            >
              <span
                className={`project-square ${p.key === project ? "purple" : ""}`}
              >
                <Folder size={13} />
              </span>
              {p.name}
              {p.key === project && <ChevronRight size={14} />}
            </button>
          ))}
        </div>
        <div className="sidebar-section">
          <div className="section-label">
            SAVED VIEWS
            <IconButton
              label="Save current view"
              onClick={() => setViewName("")}
            >
              <Plus size={14} />
            </IconButton>
          </div>
          {!board?.views.length ? (
            <p className="sidebar-hint">Keep a useful filter close by.</p>
          ) : (
            board.views.map((v) => (
              <div className="view-row" key={v.id}>
                <button
                  className="project-item"
                  onClick={() => {
                    setQuery(v.filters.q ?? "");
                    setInbox(v.filters.needs_human === "1");
                    setTag(v.filters.tag ?? "");
                    setMachine(v.filters.machine ?? "");
                    setArchived(v.filters.include_archived === "1");
                  }}
                >
                  <ListFilter size={14} />
                  {v.name}
                </button>
                <IconButton
                  label={`Delete view ${v.name}`}
                  onClick={() => {
                    void api(`/v1/views/${v.id}`, "DELETE")
                      .then(changed)
                      .catch((e) => notify(e.message, true));
                  }}
                >
                  <X size={12} />
                </IconButton>
              </div>
            ))
          )}
        </div>
        <div className="sidebar-section running-section">
          <div className="section-label">
            RUNNING NOW
            <span className="count">{board?.running.length ?? 0}</span>
          </div>
          {board?.running.length ? (
            board.running.map((r) => (
              <button
                key={r.id}
                className="running-item"
                onClick={() => openTask(r.task_id)}
              >
                <Monitor size={15} />
                <span>
                  <b>{r.machine}</b>
                  <small className="mono">
                    {r.session_name.split(":").slice(1).join(":")}
                  </small>
                  <small>
                    <span className={`dot ${r.stale ? "amber" : "green"}`} />
                    {r.task_id} ·{" "}
                    {r.stale
                      ? "Stale run"
                      : r.mode === "edit"
                        ? "Writing code"
                        : "Investigating"}
                  </small>
                </span>
              </button>
            ))
          ) : (
            <div className="quiet-state">
              <Monitor size={19} />
              <span>
                No active sessions<small>Ready for the next instruction.</small>
              </span>
            </div>
          )}
        </div>
        <div className="sidebar-bottom">
          <button
            className="settings-link"
            onClick={() => setIdentities(true)}
            aria-label="Agent identities"
          >
            <ShieldCheck size={18} />
            <span>
              Agent identities<small>Tokens and access</small>
            </span>
          </button>
          <button
            className="settings-link"
            aria-label="Project settings"
            onClick={() => {
              setSettingsTab("guidance");
              setSettings(true);
            }}
          >
            <Settings2 size={17} />
            <span>
              Project settings<small>Guidance, workflow, machines</small>
            </span>
          </button>
          <div className="profile">
            <span className="avatar">A</span>
            <span>
              <b>Andika</b>
              <small>The human · final approval</small>
            </span>
            <IconButton
              label="Sign out"
              onClick={() => {
                void api("/auth/logout", "POST").then(checkAuth);
              }}
            >
              <LogOut size={15} />
            </IconButton>
          </div>
        </div>
      </aside>
      <main className="main">
        {selected && fullPage ? (
          <TaskView
            key={selected}
            taskId={selected}
            fullPage
            onClose={closeTask}
            onOpen={openTask}
            onChange={changed}
            revision={revision}
            notify={notify}
            projects={board?.projects ?? []}
          />
        ) : (
          <>
            <header className="topbar board-topbar">
              <div className="breadcrumb">
                <Folder size={15} />
                <span>{board?.project.name ?? "Mini Kanban"}</span>
                <ChevronRight size={13} />
                <strong>{inbox ? "Needs you" : "All tasks"}</strong>
              </div>
              <div className="topbar-right">
                <span className="subtle">One instruction at a time</span>
                <span className="avatar tiny">A</span>
              </div>
            </header>
            <section className="board-heading">
              <div>
                <h1>{inbox ? "Needs you" : "All tasks"}</h1>
              </div>
              <button
                className="button primary"
                onClick={() => setNewTask("backlog")}
              >
                <Plus size={16} />
                New task
              </button>
            </section>
            <div className="board-toolbar">
              <div className="segmented">
                <button
                  className={!inbox ? "selected" : ""}
                  onClick={() => setInbox(false)}
                >
                  <KanbanSquare size={14} />
                  Board<span>{board?.counts.all ?? 0}</span>
                </button>
                <button
                  className={inbox ? "selected" : ""}
                  onClick={() => setInbox(true)}
                >
                  <Inbox size={14} />
                  Needs you
                  {board?.counts.needs_human ? (
                    <span className="purple">{board.counts.needs_human}</span>
                  ) : null}
                </button>
              </div>
              <div className="toolbar-filters">
                <label className="search">
                  <Search size={15} />
                  <input
                    aria-label="Search tasks"
                    placeholder="Search tasks & context…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                  {query && (
                    <IconButton
                      label="Clear search"
                      onClick={() => setQuery("")}
                    >
                      <X size={13} />
                    </IconButton>
                  )}
                </label>
                <label className="select-button">
                  <Monitor size={14} />
                  <select
                    aria-label="Filter by machine"
                    value={machine}
                    onChange={(e) => setMachine(e.target.value)}
                  >
                    <option value="">All machines</option>
                    {[
                      ...new Set([
                        ...(board?.running.map((r) => r.machine) ?? []),
                        ...Object.keys(board?.project.settings.machines ?? {}),
                      ]),
                    ].map((m) => (
                      <option key={m}>{m}</option>
                    ))}
                  </select>
                </label>
                <button
                  className="button subtle-button"
                  onClick={() => {
                    setSettingsTab("lanes");
                    setSettings(true);
                  }}
                >
                  <Settings2 size={14} />
                  Lanes
                </button>
              </div>
            </div>
            {filtersActive && (
              <div className="filter-summary">
                <ListFilter size={13} />
                Filtered view{tag && <Badge>#{tag}</Badge>}
                <label>
                  <input
                    type="checkbox"
                    checked={archived}
                    onChange={(e) => setArchived(e.target.checked)}
                  />
                  Include archived
                </label>
                <button className="text-button" onClick={() => setViewName("")}>
                  Save view
                </button>
                <button
                  className="text-button"
                  onClick={() => {
                    setQuery("");
                    setTag("");
                    setMachine("");
                    setArchived(false);
                  }}
                >
                  Clear filters
                </button>
              </div>
            )}
            {error ? (
              <div className="error-banner" role="alert">
                {error}
                <button onClick={() => void refresh()}>Retry</button>
              </div>
            ) : !board ? (
              <div className="board-loading">
                <LoaderCircle className="spin" /> Gathering your tasks…
              </div>
            ) : (
              <div className="board-scroll">
                <div className="board-columns">
                  {board.lanes.map((lane) => (
                    <LaneColumn
                      key={lane.key}
                      lane={lane}
                      tasks={board.tasks.filter((t) => t.lane === lane.key)}
                      onOpen={openTask}
                      onNew={() => setNewTask(lane.key)}
                      onTag={setTag}
                      onDrop={(id) => {
                        const task = board.tasks.find((t) => t.id === id);
                        if (task) void moveTask(task, lane.key);
                      }}
                      onCollapse={() => {
                        void api<BoardData["lanes"]>(
                          `/v1/projects/${project}/lanes/${lane.key}`,
                          "PATCH",
                          {
                            collapsed: !lane.collapsed,
                          },
                        )
                          .then((lanes) => {
                            setBoard((current) =>
                              current ? { ...current, lanes } : current,
                            );
                            changed();
                          })
                          .catch((e) => notify(e.message, true));
                      }}
                    />
                  ))}
                </div>
              </div>
            )}
            <footer className="board-footer">
              <span>
                <span className={`dot ${live ? "green" : "gray"}`} />
                {live ? "Changes sync automatically" : "Waiting for connection"}
              </span>
              <button
                className="text-button"
                onClick={() => setArchived(!archived)}
              >
                {archived ? "Hide archived tasks" : "Browse archived tasks"}
              </button>
              <span>
                <ShieldCheck size={13} />
                Done is always your call.
              </span>
            </footer>
          </>
        )}
      </main>
      {selected && !fullPage && (
        <Modal title={selected} wide onClose={closeTask}>
          <TaskView
            key={selected}
            taskId={selected}
            fullPage={false}
            onClose={closeTask}
            onOpen={openTask}
            onChange={changed}
            revision={revision}
            notify={notify}
            projects={board?.projects ?? []}
          />
        </Modal>
      )}
      {newTask && board && (
        <NewTask
          project={project}
          lane={newTask}
          revision={board}
          onClose={() => setNewTask(null)}
          onCreated={(task) => {
            setNewTask(null);
            changed();
            openTask(task.id);
            notify(`${task.id} created.`);
          }}
        />
      )}
      {identities && <AgentIdentities onClose={() => setIdentities(false)} />}
      {settings && board && (
        <SettingsPanel
          project={board.project}
          lanes={board.lanes}
          initialTab={settingsTab}
          onClose={() => setSettings(false)}
          onSaved={async () => {
            await refresh();
            notify("Project settings saved.");
          }}
        />
      )}
      {newProject && (
        <NewProject
          onClose={() => setNewProject(false)}
          onCreated={(p) => {
            setNewProject(false);
            chooseProject(p.key);
            changed();
          }}
        />
      )}
      {viewName !== null && (
        <Modal title="Save this view" onClose={() => setViewName(null)}>
          <form
            className="form-pad"
            onSubmit={(e) => {
              e.preventDefault();
              void saveView();
            }}
          >
            <label>
              View name
              <input
                autoFocus
                required
                value={viewName}
                onChange={(e) => setViewName(e.target.value)}
                placeholder="e.g. Ready for a second look"
              />
            </label>
            <div className="form-actions">
              <button
                type="button"
                className="button"
                onClick={() => setViewName(null)}
              >
                Cancel
              </button>
              <button className="button primary">Save view</button>
            </div>
          </form>
        </Modal>
      )}
      {toast && (
        <div
          role={toast.error ? "alert" : "status"}
          className={`toast ${toast.error ? "error" : ""}`}
        >
          {toast.error ? <CircleHelp size={17} /> : <Check size={17} />}
          <span>{toast.message}</span>
          <IconButton
            label="Dismiss notification"
            onClick={() => setToast(null)}
          >
            <X size={15} />
          </IconButton>
        </div>
      )}
    </div>
  );
}
